import { createRouter, createWebHistory } from "vue-router";
import store from "./store";
import { resetInactivityTimer } from "@/sessionManager";
import Login from "@/pages/Login";
import Main from "@/pages/Main";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Contacts from "@/pages/Contacts";
import PotentialCustomers from "@/pages/PotentialCustomers";
import Leads from "@/pages/Leads";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import AccountDetails from "@/pages/AccountDetails";
import CustomerByPhoneResolver from "@/pages/CustomerByPhoneResolver";
import UserRights from "@/pages/UserRights";
import Lock from "@/pages/Lock";
import tokenManager from "@/tokenManager";
import PriceCategories from "@/pages/PriceCategories";
import PriceCategoriesForSecondAgent from "@/pages/PriceCategoriesForSecondAgent"
import Teams from "@/pages/Teams"
import Policies from "@/pages/Policies"
import TeamTaxes from "@/pages/TeamTaxes";

import CustomerSignature from "@/pages/CustomerSignature";

const routes = [
  { path: "/:pathMatch(.*)*", redirect: { name: "Dashboard" } },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/customerSignature",
    name: "CustomerSignature",
    component: CustomerSignature,
    meta: {
      permission: "default",
    },
  },
  {
    path: "/lock",
    name: "Lock",
    component: Lock,
    meta: {
      permission: "default",
    },
  },
  {
    path: "/",
    name: "Main",
    component: Main,
    redirect: "/dashboard",
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: "/dashboard",
        name: "Dashboard",
        component: Dashboard,
        meta: {
          permission: "default",
        },
      },
      {
        path: "/customers",
        name: "Customers",
        component: Customers,
        meta: {
          teamPermission: "customer_access",
        },
      },
      {
        path: "/contacts",
        name: "Contacts",
        component: Contacts,
        meta: {
          teamPermission: "company_access",
        },
      },
      {
        path: "/potential-customers",
        name: "PotentialCustomers",
        component: PotentialCustomers,
        meta: {
          teamPermission: "customer_access",
        },
      },
      {
        path: "/leads",
        name: "Leads",
        component: Leads,
        meta: {
          teamPermission: "customer_access",
        },
      },
      {
        path: "/reports",
        name: "Reports",
        component: Reports,
        meta: {
          teamPermission: "report_access",
        },
      },
      {
        path: "/users",
        name: "Users",
        component: Users,
        meta: {
          teamPermission: "view_all_users",
        },
      },
      {
        path: "/settings",
        name: "Settings",
        component: Settings,
        meta: {
          permission: "settings_access",
        },
      },

      // !!FUTURE CHANGE          
      // 
      // path: "/account-details/:entity/:id",
      {
        path: "/account-details/customer-by-phone/:phone",
        name: "CustomerByPhoneResolver",
        component: CustomerByPhoneResolver,
        meta: {
          requiresAuth: true,
          permission: "customer_show",
        },
      },
      {
        path: "/account-details/:entity/:id/:subEntityType?/:subEntityId?",
        name: "AccountDetails",
        component: AccountDetails,
        meta: {
          requiresAuth: true,
          permission: "default",
        },
      },

      {
        path: "/user-rights",
        name: "UserRights",
        component: UserRights,
        meta: {
          teamPermission: "user_management_access",
        },
      },
      {
        path: "/price-categories",
        name: "PriceCategories",
        component: PriceCategories,
        meta: {
          teamPermission: "sales_category_access",
        },
      },
      {
        path: "/price-categories-for-second-agent",
        name: "PriceCategoriesForSecondAgent",
        component: PriceCategoriesForSecondAgent,
        meta: {
          teamPermission: "sales_category_access",
        },
      },
      {
        path: "/teams",
        name: "Teams",
        component: Teams,
        meta: {
          teamPermission: "team_access",
        },
      },
      {
        path: "/team-taxes/:id",
        name: "TeamTaxes",
        component: TeamTaxes,
        meta: {
          teamPermission: "tax_access",
          teamParam: "id",
        },
      },
      {
        path: "/policies",
        name: "Policies",
        component: Policies,
        meta: {
          teamPermission: "policy_access",
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.afterEach(() => {
  console.log("Page navigation detected.");
  resetInactivityTimer({
    store,
    timeoutMs: 60 * 60 * 1000,
  });
});

// Navigation guard with permission checking
router.beforeEach((to, from, next) => { 
  const usesHttpOnlyCookies = tokenManager.isUsingHttpOnlyCookies();
  const hasBearerToken = Boolean(localStorage.getItem("access_token"));
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!usesHttpOnlyCookies && !hasBearerToken) { 
      next({ name: "Login" });
    } else if (to.name != "AccountDetails" && sessionStorage.getItem('needs_reset') == 'true') {
      let errorCount = Number(localStorage.getItem("error_count"));
      if (errorCount > 2) {
        localStorage.setItem("error_count", "0");
        store.dispatch('logout');
      } else {
        alert("You should update your account first");
        localStorage.setItem("error_count", String(errorCount + 1));
      }
    } else {
      const isSuperAdmin = store.getters.isSuperAdmin;
      const authorizationLoaded = store.getters.authorizationLoaded;
      const routeTeamId = to.meta.teamParam ? Number(to.params[to.meta.teamParam]) : null;
      const teamIds = to.meta.teamPermission && routeTeamId === null
        ? store.getters.authorizedTeamIds(to.meta.teamPermission)
        : [];

      // For AccountDetails, add entity-specific permission checks
      let hasEntityAccess = true;
      if (to.name === "AccountDetails") {
  
        // Super admin always has access
        if (isSuperAdmin) {
          hasEntityAccess = true; 
        } else {
          // For regular users, the component-level check will validate team intersection
          // Here we just log the navigation attempt 
          hasEntityAccess = true; // Allow navigation, let component handle entity validation
        }
      }

      const isEntityRoute = to.name === "AccountDetails";
      const globalPermissionKey = to.meta.globalPermission || to.meta.permission;
      const hasRoutePermission = isEntityRoute
        ? true
        : !authorizationLoaded
          ? true
          : isSuperAdmin
            ? true
            : globalPermissionKey && globalPermissionKey !== "default"
              ? store.getters.hasGlobalPermission(globalPermissionKey)
              : to.meta.teamPermission && to.meta.teamPermission !== "default"
                ? routeTeamId !== null
                  ? store.getters.hasTeamPermission(to.meta.teamPermission, routeTeamId)
                  : teamIds === null || teamIds.length > 0
                : true;

      // The backend remains authoritative while an older session is loading its
      // first snapshot. Once loaded, only scoped selectors decide coarse access.
      if (hasRoutePermission && hasEntityAccess) {
        next();
      } else if (to.name === "Dashboard") {
        next();
      } else {
        next({ name: "Dashboard" });
      }
    }
  } else if (to.name === "Login" && !usesHttpOnlyCookies && hasBearerToken) { 
    next({ name: "Dashboard" });
  } else {
    next();
  }
});

export default router;
